import React, { useEffect, useState } from "react";
import {
  Inbox,
  Timer,
  TrendingUp,
  AlertTriangle,
  Sparkles,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DASH } from "@/constants/testIds";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import { CHANNEL_COLORS } from "@/lib/api";

const CHART_COLORS = ["#B84221", "#D97736", "#78866B", "#1E293B", "#EAB308"];

function MetricCard({ tid, label, value, sub, icon: Icon, tint = "primary" }) {
  return (
    <div
      data-testid={tid}
      className="paper-card p-5 flex flex-col gap-3 hover:-translate-y-0.5 hover:border-stone-300 transition-all duration-200"
    >
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        <div
          className={`w-8 h-8 rounded-lg grid place-items-center ${
            tint === "primary"
              ? "bg-terracotta/10 text-terracotta"
              : tint === "amber"
                ? "bg-amber-50 text-amber-600"
                : tint === "sage"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-stone-100 text-stone-600"
          }`}
        >
          <Icon className="w-4 h-4" strokeWidth={2} />
        </div>
      </div>
      <div className="font-serif text-4xl leading-none text-stone-900">{value}</div>
      {sub && <div className="text-xs text-stone-500">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [seeding, setSeeding] = useState(false);

  const seedDemo = async () => {
    if (seeding) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed-demo-inquiries");
      toast.success(`Seeded ${data.created} demo inquiries`, {
        description:
          "Auto-match is running — refresh the inbox in ~15 seconds to see matched sarees.",
      });
      setTimeout(load, 800);
    } catch (e) {
      toast.error("Could not seed demo inquiries", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSeeding(false);
    }
  };

  const load = async () => {
    try {
      const [a, f] = await Promise.all([
        api.get("/analytics/dashboard"),
        api.get("/inquiries-follow-ups/pending"),
      ]);
      setAnalytics(a.data);
      setFollowups(f.data || []);
    } catch (err) {
      console.warn('Saheli: dashboard load failed', err);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // auto-refresh
    return () => clearInterval(t);
  }, []);

  if (!analytics) {
    return <div className="text-stone-500 text-sm">Loading dashboard…</div>;
  }

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="overline">Silk Mart Sarees · Live</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-stone-900 mt-1">
            Namaste, welcome to Saheli
          </h1>
          <p className="text-stone-500 text-sm mt-2 max-w-lg">
            Every inquiry, every match — one loom of conversation.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="dashboard-seed-demo"
            onClick={seedDemo}
            disabled={seeding}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-stone-200 bg-white hover:border-terracotta hover:text-terracotta text-stone-600 transition-colors disabled:opacity-60"
            title="Wipe and re-seed 6 lifelike demo inquiries across channels"
          >
            <Wand2 className="w-3.5 h-3.5" />
            {seeding ? "Seeding…" : "Seed demo inquiries"}
          </button>
          <div className="flex items-center gap-2 text-xs text-stone-500">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            Auto-refreshing every 15 seconds
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <MetricCard
          tid={DASH.cardToday}
          label="Today's inquiries"
          value={analytics.total_today}
          sub={`Total ever: ${analytics.total_inquiries}`}
          icon={Inbox}
          tint="primary"
        />
        <MetricCard
          tid={DASH.cardResponse}
          label="Avg response time"
          value={
            analytics.avg_response_minutes
              ? `${analytics.avg_response_minutes}m`
              : "—"
          }
          sub="Today, replied inquiries"
          icon={Timer}
          tint="sage"
        />
        <MetricCard
          tid={DASH.cardConversion}
          label="Conversion rate"
          value={`${analytics.conversion_rate}%`}
          sub={`${analytics.confirmed_count} confirmed`}
          icon={TrendingUp}
          tint="amber"
        />
        <MetricCard
          tid={DASH.cardFollowups}
          label="Follow-ups pending"
          value={analytics.follow_ups_pending}
          sub=">24h without reply"
          icon={AlertTriangle}
          tint={analytics.follow_ups_pending ? "amber" : "sage"}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div
          data-testid={DASH.chartFabrics}
          className="paper-card p-5 lg:col-span-2"
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline">Most-requested fabrics</div>
              <h3 className="font-serif text-xl text-stone-800 mt-1">
                Catalog composition
              </h3>
            </div>
            <Sparkles className="w-4 h-4 text-terracotta" />
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={analytics.top_fabrics}
                margin={{ top: 10, right: 10, bottom: 0, left: -10 }}
              >
                <XAxis
                  dataKey="fabric"
                  tick={{ fontSize: 11, fill: "#78716c" }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#78716c" }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "#fff",
                    border: "1px solid #e7e5e4",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                  {(analytics.top_fabrics || []).map((row) => (
                    <Cell
                      key={row.fabric}
                      fill={CHART_COLORS[
                        analytics.top_fabrics.indexOf(row) % CHART_COLORS.length
                      ]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {(analytics.price_ranges || []).map((r) => (
              <div key={r.range} className="heritage-badge">
                {r.range} · {r.count}
              </div>
            ))}
          </div>
        </div>

        <div data-testid={DASH.chartChannels} className="paper-card p-5">
          <div className="overline">Today by channel</div>
          <h3 className="font-serif text-xl text-stone-800 mt-1 mb-3">
            Where they came from
          </h3>
          <div className="h-56">
            {analytics.by_channel?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analytics.by_channel}
                    dataKey="count"
                    nameKey="channel"
                    innerRadius={45}
                    outerRadius={75}
                    paddingAngle={2}
                  >
                    {analytics.by_channel.map((row) => (
                      <Cell
                        key={row.channel}
                        fill={CHART_COLORS[
                          analytics.by_channel.indexOf(row) % CHART_COLORS.length
                        ]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "#fff",
                      border: "1px solid #e7e5e4",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 11 }}
                    iconType="circle"
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm text-stone-400">
                No inquiries yet today
              </div>
            )}
          </div>
        </div>
      </div>

      <div data-testid={DASH.followupsList} className="paper-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Follow-up reminders</div>
            <h3 className="font-serif text-xl text-stone-800 mt-1">
              No reply after 24 hours
            </h3>
          </div>
          <div className="text-xs text-stone-500">{followups.length} pending</div>
        </div>
        {followups.length === 0 ? (
          <div className="text-sm text-stone-400 py-6 text-center">
            You're all caught up ✿
          </div>
        ) : (
          <div className="divide-y divide-stone-100">
            {followups.slice(0, 6).map((f) => (
              <div
                key={f.id}
                className="py-3 flex items-center gap-3 hover:bg-stone-50 rounded-lg px-2 -mx-2"
              >
                <div className={`heritage-badge ${CHANNEL_COLORS[f.channel] || ""}`}>
                  {f.channel}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-stone-800 truncate">
                    {f.customer_name}{" "}
                    <span className="text-stone-400">· {f.inquiry_type}</span>
                  </div>
                  <div className="text-xs text-stone-500 truncate">
                    {f.message || "—"}
                  </div>
                </div>
                <div className="text-[11px] text-stone-400">
                  {new Date(f.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
